import { alpha, buttonClasses, CircularProgress, lighten, ThemeOptions } from '@mui/material';

import React from 'react';
import { circularProgressSizeProps } from '../../../utils';
import { capitalDesignTokens } from '../../capital_design_tokens';

// tslint:disable:interface-name
declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    extraSmall: true;
  }
}

export const MuiButton: ThemeOptions['components']['MuiButton'] = {
  defaultProps: {
    disableElevation: true,
    loadingIndicator: (
      <CircularProgress
        color="inherit"
        size={circularProgressSizeProps.extraSmall.size}
        thickness={circularProgressSizeProps.extraSmall.thickness}
        disableShrink={true}
      />
    )
  },
  styleOverrides: {
    text: ({ theme }) => {
      return {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'transparent',
        [`&.${buttonClasses.colorError}`]: {
          '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.error.dark,
            color: theme.palette.common.white
          }
        }
      };
    },
    root: ({ ownerState, theme }) => {
      return {
        minWidth: 0,
        ...(theme.palette[ownerState.color] && {
          [`&.${buttonClasses.disabled}`]: {
            [`&.${buttonClasses.outlined}`]: {
              color: lighten(theme.palette[ownerState.color].main, 0.4),
              backgroundColor: lighten(theme.palette[ownerState.color].main, 0.9),
              borderColor: lighten(theme.palette[ownerState.color].main, 0.9)
            },
            [`&.${buttonClasses.outlined}.${buttonClasses.colorSecondary}`]: {
              borderColor: lighten(theme.palette[ownerState.color].main, 0.75)
            },
            [`&.${buttonClasses.contained}`]: {
              color: theme.palette.common.white,
              backgroundColor: lighten(theme.palette[ownerState.color].main, 0.5)
            },
            [`&.${buttonClasses.colorError}`]: {
              color: theme.palette.common.white,
              backgroundColor: lighten(theme.palette[ownerState.color].main, 0.5)
            },
            [`&.${buttonClasses.text}`]: {
              color: lighten(theme.palette[ownerState.color].main, 0.4),
              backgroundColor: 'transparent'
            },
            [`&.${buttonClasses.loading}.${buttonClasses.loadingPositionCenter}`]: {
              color: 'transparent'
            },
            // Do not remove - this is for backwards compatibility with the lab version of loading button
            [`&.MuiLoadingButton-loading:has(.MuiLoadingButton-loadingIndicatorCenter)`]: {
              color: 'transparent'
            }
          }
        }),
        '&:hover': {
          ...((ownerState.variant === 'outlined' || ownerState.variant === 'text') &&
            ownerState.color === 'primary' && {
              backgroundColor: theme.palette.action.hover
            })
        },
        [`&.${buttonClasses.focusVisible}, &:focus-visible`]: {
          // Background colors needed for cases where ripple effect is disabled
          ...(theme.palette[ownerState.color] && {
            ...(ownerState.variant === 'contained' &&
              (ownerState.color === 'primary' || ownerState.color === 'error') && {
                backgroundColor: theme.palette[ownerState.color]?.dark
              }),
            ...((ownerState.variant === 'outlined' || ownerState.variant === 'text') &&
              ownerState.color !== 'error' && {
                backgroundColor:
                  ownerState.color === 'primary'
                    ? theme.palette.action.hover
                    : alpha(theme.palette[ownerState.color]?.light, theme.palette.action.hoverOpacity)
              })
          }),
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: capitalDesignTokens.foundations.units.unitQuarter
        },
        // Do not remove - this is for backwards compatibility with the lab version of loading button
        // We offset this from the original 14px down to 10px to account for 4px margin change on start and end icons
        '& .MuiLoadingButton-loadingIndicatorStart': {
          left: '10px'
        },
        '& .MuiLoadingButton-loadingIndicatorEnd': {
          right: '10px'
        }
      };
    },
    loadingIndicator: ({ theme, ownerState }) => {
      return {
        ...(ownerState.loadingPosition === 'center' && {
          color: theme.palette.common.white,
          ...(ownerState.variant === 'outlined' && {
            color: lighten(theme.palette.primaryShadowed.main, 0.4)
          })
        }),
        // We offset this from the original 14px down to 10px to account for 4px margin change on start and end icons
        ...(ownerState.loadingPosition === 'start' && {
          left: '10px'
        }),
        ...(ownerState.loadingPosition === 'end' && {
          right: '10px'
        })
      };
    },
    startIcon: ({ ownerState: { startIcon, endIcon } }) => [
      !!startIcon && { marginRight: 4 },
      !!endIcon && { marginLeft: 4 }
    ],
    endIcon: ({ ownerState: { startIcon, endIcon } }) => [
      !!startIcon && { marginRight: 4 },
      !!endIcon && { marginLeft: 4 }
    ],
    sizeSmall: ({ theme }) => ({
      fontSize: capitalDesignTokens.foundations.typography.fontSize.bodyXSmall,
      padding: theme.spacing(0.375, 1),
      minHeight: `${capitalDesignTokens.componentSizes.small}px`
    }),
    sizeMedium: ({ theme }) => ({
      fontSize: capitalDesignTokens.foundations.typography.fontSize.bodyDefault,
      padding: theme.spacing(0.5, 1.5),
      minHeight: `${capitalDesignTokens.componentSizes.medium}px`
    }),
    sizeLarge: ({ theme }) => ({
      fontSize: capitalDesignTokens.componentFontSizes.large,
      padding: theme.spacing(0.875, 2),
      minHeight: `${capitalDesignTokens.componentSizes.large}px`
    })
  },
  variants: []
};
